/* eslint-disable react-hooks/exhaustive-deps */
import { getTicketsLockedByUser, lockNewTicketForUser, unlockATicketForUser } from 'api/generalTicket'
import { debounce } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Ticket } from 'types/Event'

interface UseAutoTicketLockingParams {
    ticketsLockedByUser: Ticket[]
    sessionId: string
    eventId: string
    onSelectedTicketsChange: (selectedTickets: string[]) => void
    onLocksDone?: () => void
}

export const useAutoTicketLockingForGeneralV2 = ({
    ticketsLockedByUser,
    sessionId,
    eventId,
    onSelectedTicketsChange,
    onLocksDone,
}: UseAutoTicketLockingParams) => {
    const lastAmountRef = useRef<number>(0)
    const [selectedTickets, setSelectedTickets] = useState<string[]>([])
    const [amount, setAmount] = useState<number>(ticketsLockedByUser?.length || 0)

    // Queue reference to chain lock/unlock requests
    const queueRef = useRef<Promise<void>>(Promise.resolve())
    const cancelledRef = useRef(false)

    useEffect(() => {
        setAmount(ticketsLockedByUser?.length || 0)
    }, [ticketsLockedByUser])

    // Queue wrapper for safe chaining
    const enqueueLockUnlock = useCallback((task: () => Promise<void>) => {
        queueRef.current = queueRef.current
            .then(() => task())
            .catch((err) => {
                console.error('Error in lock/unlock queue:', err)
            })
        return queueRef.current
    }, [])

    // Debounced update to prevent spamming the API on fast input changes
    const debouncedUpdateLocks = useCallback(
        debounce(async (targetAmount: number) => {
            if (!sessionId) return
            cancelledRef.current = false

            try {
                const ticketsLocked = await getTicketsLockedByUser(eventId, sessionId)
                const currentlyLockedCount = ticketsLocked.length
                const diff = targetAmount - currentlyLockedCount

                // Function declared outside loop for ESLint safety
                const lockTask = async () => {
                    if (cancelledRef.current) return
                    await lockNewTicketForUser(eventId, sessionId)
                }

                const unlockTask = async () => {
                    if (cancelledRef.current) return
                    await unlockATicketForUser(eventId, sessionId)
                }

                if (diff > 0) {
                    for (let i = 0; i < diff; i++) {
                        await enqueueLockUnlock(lockTask)
                    }
                } else if (diff < 0) {
                    for (let i = 0; i < -diff; i++) {
                        await enqueueLockUnlock(unlockTask)
                    }
                }

                if (!cancelledRef.current) {
                    const refreshed = await getTicketsLockedByUser(eventId, sessionId)
                    const ticketIds = refreshed.map((t: Ticket) => t.ticket_id)
                    setSelectedTickets(ticketIds)
                    onSelectedTicketsChange(ticketIds)
                }
            } catch (err) {
                console.error('Error updating ticket locks:', err)
            } finally {
                if (!cancelledRef.current && onLocksDone) onLocksDone()
            }
        }, 200),
        [eventId, sessionId, enqueueLockUnlock, onSelectedTicketsChange, onLocksDone],
    )

    useEffect(() => {
        if (!sessionId || amount === lastAmountRef.current) return
        lastAmountRef.current = amount
        debouncedUpdateLocks(amount)

        return () => {
            cancelledRef.current = true
            debouncedUpdateLocks.cancel()
        }
    }, [amount, sessionId, debouncedUpdateLocks])

    return { selectedTickets, amount, setAmount }
}
